import { Outlet, NavLink } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Dumbbell,
  MessageSquare,
  Calculator,
  Menu,
  X,
  LogOut,
  User,
  Sun,
  Moon,
} from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';
import { useTheme } from '../context/ThemeContext';
import { NotificationBell } from './NotificationBell';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/students', icon: Users, label: 'Alumnos' },
  { to: '/exercises', icon: Dumbbell, label: 'Ejercicios' },
  { to: '/calculators', icon: Calculator, label: 'Calculadoras' },
  { to: '/comments', icon: MessageSquare, label: 'Comentarios' },
];

export function Layout() {
  const { user, logout } = useAuth0();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-dark-900/70 dark:bg-dark-900/70 light:bg-white/70 backdrop-blur-xl border-b border-dark-700/50 dark:border-dark-700/50 light:border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <NavLink to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/20 group-hover:shadow-primary-500/40 transition-shadow">
                <Dumbbell className="w-5 h-5 text-white" />
              </div>
              <span className="font-display text-xl font-bold text-gradient hidden sm:block">
                RUTINEO
              </span>
            </NavLink>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.to === '/'} className="relative">
                  {({ isActive }) => (
                    <span
                      className={clsx(
                        'relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200',
                        isActive
                          ? 'text-primary-400'
                          : 'text-dark-300 dark:text-dark-300 light:text-zinc-600 hover:text-dark-100 dark:hover:text-dark-100 light:hover:text-zinc-900'
                      )}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="nav-pill"
                          className="absolute inset-0 -z-10 rounded-xl bg-primary-500/10 border border-primary-500/20"
                          transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                        />
                      )}
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* User menu */}
            <div className="flex items-center gap-2">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-dark-300 dark:text-dark-300 light:text-zinc-600 hover:bg-dark-800 dark:hover:bg-dark-800 light:hover:bg-zinc-100 transition-colors"
                title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>

              {/* Notificaciones */}
              <NotificationBell />

              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-dark-800 dark:hover:bg-dark-800 light:hover:bg-zinc-100 transition-colors"
                >
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-8 h-8 rounded-lg"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary-400" />
                    </div>
                  )}
                  <span className="hidden sm:block text-sm text-dark-200 dark:text-dark-200 light:text-zinc-700">
                    {user?.name || user?.email}
                  </span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 py-1 bg-dark-800 dark:bg-dark-800 light:bg-white border border-dark-600 dark:border-dark-600 light:border-zinc-200 rounded-xl shadow-xl">
                    <div className="px-3 py-2 border-b border-dark-600 dark:border-dark-600 light:border-zinc-200">
                      <p className="text-sm font-medium text-dark-100 dark:text-dark-100 light:text-zinc-900">{user?.name}</p>
                      <p className="text-xs text-dark-400 dark:text-dark-400 light:text-zinc-500 truncate">{user?.email}</p>
                    </div>
                    <NavLink
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-dark-300 dark:text-dark-300 light:text-zinc-600 hover:text-dark-100 dark:hover:text-dark-100 light:hover:text-zinc-900 hover:bg-dark-700 dark:hover:bg-dark-700 light:hover:bg-zinc-100 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      Mi Perfil
                    </NavLink>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-dark-300 dark:text-dark-300 light:text-zinc-600 hover:text-dark-100 dark:hover:text-dark-100 light:hover:text-zinc-900 hover:bg-dark-700 dark:hover:bg-dark-700 light:hover:bg-zinc-100 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-xl text-dark-300 dark:text-dark-300 light:text-zinc-600 hover:text-dark-100 dark:hover:text-dark-100 light:hover:text-zinc-900 hover:bg-dark-800 dark:hover:bg-dark-800 light:hover:bg-zinc-100 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-dark-700/50 dark:border-dark-700/50 light:border-zinc-200 bg-dark-900/95 dark:bg-dark-900/95 light:bg-white/95"
          >
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
                      isActive
                        ? 'bg-primary-500/10 text-primary-400'
                        : 'text-dark-300 dark:text-dark-300 light:text-zinc-600 hover:text-dark-100 dark:hover:text-dark-100 light:hover:text-zinc-900 hover:bg-dark-800 dark:hover:bg-dark-800 light:hover:bg-zinc-100'
                    )
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </header>

      {/* Click outside to close menus */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setUserMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-dark-700/50 dark:border-dark-700/50 light:border-zinc-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-dark-500 dark:text-dark-500 light:text-zinc-500 text-sm">
            © {new Date().getFullYear()} RUTINEO. Gestión de entrenamientos profesional.
          </p>
        </div>
      </footer>
    </div>
  );
}
